import { useState, useEffect } from "react";
import { useParams, useNavigate, Outlet, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Building2, MapPin, Image, Target, DollarSign, 
  ChevronLeft, Phone, Mail, Globe, Calendar,
  TrendingUp, Eye, MousePointer, Users
} from "lucide-react";
import { Link } from "react-router-dom";

const tiers = {
  GOLD: { label: "Gold", color: "bg-yellow-500" },
  SILVER: { label: "Silver", color: "bg-gray-400" },
  BRONZE: { label: "Bronze", color: "bg-orange-600" },
};

const statuses = {
  ACTIVE: { label: "Active", color: "bg-green-500" },
  INACTIVE: { label: "Inactive", color: "bg-gray-500" },
  SUSPENDED: { label: "Suspended", color: "bg-red-500" },
  TRIAL: { label: "Trial", color: "bg-blue-500" },
  PENDING: { label: "Pending", color: "bg-yellow-500" },
};

export default function AdvertiserContext() {
  const { advertiserId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [advertiser, setAdvertiser] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Determine if we're on the main company info page or a sub-page
  const isCompanyInfoPage = location.pathname === `/admin/advertisers/${advertiserId}`;

  useEffect(() => {
    fetchAdvertiser();
    fetchStats();
  }, [advertiserId]);

  const fetchAdvertiser = async () => {
    try {
      const response = await axios.get(`${API}/accounts/${advertiserId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdvertiser(response.data);
    } catch (error) {
      toast.error("Failed to fetch advertiser");
      navigate("/admin/advertisers");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      // Fetch counts for this advertiser using scoped endpoints
      const [locationsRes, creativesRes, campaignsRes] = await Promise.all([
        axios.get(`${API}/accounts/${advertiserId}/locations`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/accounts/${advertiserId}/creatives`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/accounts/${advertiserId}/campaigns`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
      ]);
      
      setStats({
        locations: locationsRes.data?.length || 0,
        creatives: creativesRes.data?.length || 0,
        campaigns: campaignsRes.data?.length || 0,
        activeCampaigns: campaignsRes.data?.filter(c => c.status === 'ACTIVE')?.length || 0,
      });
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse text-primary">Loading advertiser...</div>
      </div>
    );
  }

  if (!advertiser) {
    return null;
  }

  const tier = tiers[advertiser.tier] || { label: advertiser.tier, color: "bg-gray-500" };
  const status = statuses[advertiser.status] || { label: advertiser.status, color: "bg-gray-500" };

  const navItems = [
    { path: `/admin/advertisers/${advertiserId}`, label: "Company Info", icon: Building2 },
    { path: `/admin/advertisers/${advertiserId}/locations`, label: "Locations", icon: MapPin, count: stats?.locations },
    { path: `/admin/advertisers/${advertiserId}/creatives`, label: "Creatives", icon: Image, count: stats?.creatives },
    { path: `/admin/advertisers/${advertiserId}/campaigns`, label: "Campaigns", icon: Target, count: stats?.campaigns },
    { path: `/admin/advertisers/${advertiserId}/billing`, label: "Billing", icon: DollarSign },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Back Button + Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate("/admin/advertisers")}
          className="shrink-0"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Advertisers
        </Button>
      </div>

      {/* Advertiser Header Card */}
      <Card className="bg-card border-border">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="p-4 rounded-xl bg-primary/10">
                <Building2 className="h-8 w-8 text-primary" />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl font-bold">{advertiser.business_name}</h1>
                  <Badge className={`${tier.color} text-white`}>{tier.label}</Badge>
                  <Badge variant="outline">
                    <span className={`w-2 h-2 rounded-full ${status.color} mr-1.5`}></span>
                    {status.label}
                  </Badge>
                </div>
                <p className="text-muted-foreground">
                  {advertiser.primary_location ? (
                    <>
                      {advertiser.primary_location.suburb}, {advertiser.primary_location.state} {advertiser.primary_location.postcode}
                    </>
                  ) : (
                    <span className="text-yellow-500">No primary location set</span>
                  )}
                  {" "}• ABN: {advertiser.abn}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sub Navigation */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {navItems.map(item => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          return (
            <Link key={item.path} to={item.path}>
              <Button 
                variant={isActive ? "default" : "outline"} 
                size="sm"
                className="shrink-0"
              >
                <Icon className="h-4 w-4 mr-2" />
                {item.label}
                {item.count !== undefined && (
                  <Badge variant="secondary" className="ml-2 h-5 px-1.5">
                    {item.count}
                  </Badge>
                )}
              </Button>
            </Link>
          );
        })}
      </div>

      {/* Content Area */}
      {isCompanyInfoPage ? (
        <CompanyInfo advertiser={advertiser} stats={stats} />
      ) : (
        <Outlet context={{ advertiser, advertiserId, refreshStats: fetchStats, refreshAdvertiser: fetchAdvertiser }} />
      )}
    </div>
  );
}

// Company Info Component (landing page when clicking an advertiser)
function CompanyInfo({ advertiser, stats }) {
  const { token } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    fetchAnalytics();
    fetchBalance();
  }, [advertiser.id]);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/summary?account_id=${advertiser.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      // Use mock data if endpoint doesn't support account_id filter
      setAnalytics({
        impressions: Math.floor(Math.random() * 50000) + 10000,
        clicks: Math.floor(Math.random() * 2000) + 500,
        engagements: Math.floor(Math.random() * 500) + 100,
        cpdc_conversions: Math.floor(Math.random() * 50) + 10,
        spend: Math.floor(Math.random() * 5000) + 1000,
      });
    }
  };

  const fetchBalance = async () => {
    try {
      const response = await axios.get(`${API}/billing/balance?account_id=${advertiser.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBalance(response.data);
    } catch (error) {
      setBalance({ balance: 0, currency: "AUD" });
    }
  };

  const primaryContact = advertiser.contacts?.find(c => c.is_primary) || advertiser.contacts?.[0];

  return (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <MapPin className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.locations || 0}</p>
                <p className="text-xs text-muted-foreground">Locations</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Image className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.creatives || 0}</p>
                <p className="text-xs text-muted-foreground">Creatives</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <Target className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.activeCampaigns || 0}</p>
                <p className="text-xs text-muted-foreground">Active Campaigns</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-500/10">
                <DollarSign className="h-5 w-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">${balance?.balance?.toLocaleString() || 0}</p>
                <p className="text-xs text-muted-foreground">Balance</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Business Details */}
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              Business Details
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Business Name</p>
                  <p className="font-medium">{advertiser.business_name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">ABN</p>
                  <p className="font-medium">{advertiser.abn}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Tier</p>
                  <Badge className={`${tiers[advertiser.tier]?.color || 'bg-gray-500'} text-white`}>
                    {tiers[advertiser.tier]?.label || advertiser.tier}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Status</p>
                  <Badge variant="outline">
                    <span className={`w-2 h-2 rounded-full ${statuses[advertiser.status]?.color || 'bg-gray-500'} mr-1.5`}></span>
                    {statuses[advertiser.status]?.label || advertiser.status}
                  </Badge>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Address</p>
                <p className="font-medium">
                  {advertiser.location?.suburb}, {advertiser.location?.state} {advertiser.location?.postcode}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Member Since</p>
                <p className="font-medium flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  {new Date(advertiser.created_at || Date.now()).toLocaleDateString('en-AU', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Primary Contact */}
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Primary Contact
            </h3>
            {primaryContact ? (
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Name</p>
                  <p className="font-medium">{primaryContact.name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Role</p>
                  <p className="font-medium capitalize">{primaryContact.role || 'Primary Contact'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Email</p>
                  <p className="font-medium flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    {primaryContact.email}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Phone</p>
                  <p className="font-medium flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    {primaryContact.phone || 'Not provided'}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground">No contact information available</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Performance Metrics */}
      <Card className="bg-card border-border">
        <CardContent className="p-6">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Performance Metrics (All Time)
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-4 rounded-lg bg-muted/30">
              <Eye className="h-6 w-6 mx-auto mb-2 text-blue-500" />
              <p className="text-2xl font-bold">{analytics?.impressions?.toLocaleString() || 0}</p>
              <p className="text-xs text-muted-foreground">Impressions</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-muted/30">
              <MousePointer className="h-6 w-6 mx-auto mb-2 text-green-500" />
              <p className="text-2xl font-bold">{analytics?.clicks?.toLocaleString() || 0}</p>
              <p className="text-xs text-muted-foreground">Clicks</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-muted/30">
              <TrendingUp className="h-6 w-6 mx-auto mb-2 text-purple-500" />
              <p className="text-2xl font-bold">
                {analytics?.impressions > 0 ? ((analytics.clicks / analytics.impressions) * 100).toFixed(2) : 0}%
              </p>
              <p className="text-xs text-muted-foreground">CTR</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-muted/30">
              <Users className="h-6 w-6 mx-auto mb-2 text-yellow-500" />
              <p className="text-2xl font-bold">{analytics?.cpdc_conversions?.toLocaleString() || 0}</p>
              <p className="text-xs text-muted-foreground">Conversions</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-muted/30">
              <DollarSign className="h-6 w-6 mx-auto mb-2 text-emerald-500" />
              <p className="text-2xl font-bold">${analytics?.spend?.toLocaleString() || 0}</p>
              <p className="text-xs text-muted-foreground">Total Spend</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
